import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, ListItemText, ListItem, List, TextField, CircularProgress, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { StyledButton, StyledHeadingGrid, StyledGridContainer, StyledNotesButton, StyledPaper } from "./styles"

//Components
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";
import { getFormattedDate } from '../../utils/getFormattedDateTime';
import mapProjectsData from '../../utils/mapProjectsData';

//cutom hooks
import { useFetch } from '../../hooks/useFetch';
import { useUpdate } from '../../hooks/useUpdate';

//constants
import { API_URLS } from '../../Constants';

//context
import { useAuth } from '../../context/AuthContext';
import { getCombRegImpactedRegs } from '../../utils/getCombRegImpactedRegs';

//Breadcrumb path items for review project.
const reviewPathItems = [
  "Dashboard",
  "Projects",
  "Review Project"
]

//Breadcrumb path items for view project.
const viewPathItems = [
  "Dashboard",
  "Projects",
  "View Project"
]

//Invoked for viewing or reviewing a selected project.
export default function ReviewProject(props) {
  const { auth } = useAuth();

  //React hooks
  const [project, setProject] = useState([]);
  const [edit, setEdit] = useState(false);
  const [notes, setNotes] = useState("");
  const [updatedNotes, setUpdatedNotes] = useState("");
  const [projectPhase, setProjectPhase] = useState([]);
  const [connectedRoadways, setConnectedRoadways] = useState([]);
  const fetcher = useFetch("get", API_URLS["API_GET_PROJECT_BY_ID"]);
  const postFetcher = useUpdate("post", API_URLS["API_ADD_UPDATE_PROJECT"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [{ projectId, component }] = useState(() => location.state || {});
  const isReview = component === 'Review'; //Set value as per page.
  const [errors, setErrors] = useState([]);

  //Fetch data from API when the component renders.
  useEffect(() => {

    //If no project id is found in the url, navigate to view projects page.
    if (!projectId) {
      navigate('/view-projects');
      return;
    }
    const url = `${API_URLS["API_GET_PROJECT_BY_ID"]}?id=${projectId}`;
    fetcher.setRequest({ passedUrl: url });
  }, []);

  //Once response is received, set appropriate state variables.
  useEffect(() => {
    const response = fetcher.serverResponse;
    if (response?.length > 0) {
      setProject(response);
    }
  }, [fetcher.serverResponse]);

  useEffect(() => {
    if (project?.length > 0) {
      setProjectPhase(project[0].projectDetails);
      setConnectedRoadways(project[0].connectedRoadways);
      setNotes(project[0].notes);
      setUpdatedNotes(project[0].notes);
    }
  }, [project]);

  //Invoked when edit icon is clicked.
  const handleEditNotes = () => {
    setEdit(true);
  };

  //Imvoked when notes text area changes.
  const handleUpdateNotes = (e) => {
    setUpdatedNotes(e.target.value);
  };

  //Invoked when Cancel button is clicked.
  const handleCancelBtnClick = () => {
    navigate('/view-projects');
  }

  //Invoked when "Good As Is" button is clicked.
  const handleGoodAsIs = async () => {
    const currentDateTime = new Date();

    //Update fields as applicable.
    if (project[0].notes !== updatedNotes) {
      project[0].notes = updatedNotes;
      project[0].lastModifiedDate = currentDateTime;
    }
    const reviewedBy = auth?.user?.username || "";

    //Map form object into database object
    const options = {
      projectData: project[0],
      projectStatus: project[0].status,
      passedCreatedDate: project[0].createdDate,
      passedYrFirstEntered: project[0].yearFirstEntered,
      passedYrPlanActive: project[0].yearPlanActive,
      passedQtrPlanActive: project[0].quarterPlanActive,
      passedReviewedDate: currentDateTime,
      passedReviewedBy: reviewedBy,
      passedLastModifiedDate: project[0].lastModifiedDate
    }
    const mappedProjectsData = mapProjectsData(options);

    await postFetcher.executeUpdate(mappedProjectsData);
    if (postFetcher?.serverResponse?.error) {
      console.error('Error in POST request: ', postFetcher.serverResponse.error);
    }
  }


  useEffect(() => {

    if (postFetcher?.serverError && postFetcher?.serverError?.message?.validationErrors) {
      const errorSet = transformStrings(postFetcher.serverError.message.validationErrors);

      setErrors(errorSet);
    }


  }, [postFetcher.serverError]);

  function transformStrings(inputArray) {
    const transformedArray = inputArray.map(inputString => {
      if (inputString.field.includes("projectDetails")) {
        inputString.field = inputString.field.replace("projectDetails", "projectPhase");
      }
      const parts = inputString.field.split('/').map(part => part.replace(/\d+/, '-'));

      // Join with spaces before each camel case letter and capitalize the first letter
      return parts
        .map(part =>
          part.replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space before camel case letters
            .replace(/\b\w/g, char => char.toUpperCase()) // Capitalize each word
        )
        .join(' ');
    });

    // Ensure unique values in the final output array
    return Array.from(new Set(transformedArray));
  }


  //Navigate to view projects is response received successfully
  useEffect(() => {
    if (postFetcher?.serverResponse)
      navigate('/view-projects');
    else if (postFetcher?.serverResponse?.error)
      console.error('Error in POST request: ', postFetcher.serverResponse.error);
  }, [postFetcher.serverResponse]);

  return (
    <Grid container spacing={2}>

      {/* Breadcrumbs for the page */}
      <Grid item xs={12} md={12}>
        <RouterBreadcrumbs pathItems={isReview ? reviewPathItems : viewPathItems} />
      </Grid>

      {/* Page Title for the page */}
      <Grid item xs={12} md={12}>
        <Title title={isReview ? "Review Project" : "View Project"} />
      </Grid>

      {fetcher.isLoading ? (
        <Grid container justifyContent="center" alignItems="center" style={{ height: '80vh' }}>
          <CircularProgress />
        </Grid>
      ) : project.length > 0 && (
        <>
          {!isReview && (
            <Grid item xs={12} sx={{ paddingRight: "10px" }}>
              <Alert severity="warning">
                This page is <strong>view-only</strong>. To edit information of this project, please click <Link to={`/edit-project?id=${projectId}`} style={{ color: 'inherit', fontWeight: 'bold' }}>here</Link>.
              </Alert>
            </Grid>
          )}
          {/* Project Information Heading */}
          <StyledHeadingGrid item xs={12} sx={{ marginTop: "0px" }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              Project Information
            </Typography>
          </StyledHeadingGrid>

          {/* Display Project Information */}
          <Grid item xs={12} md={12}>
            <StyledPaper elevation={3}>
              <Grid container spacing={2}>
                <Grid item xs={4} md={4} sx={{ minWidth: 0 }}>
                  <List role="presentation">
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Project Title</b>}
                        secondary={project[0].projectTitle || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Project From</b>}
                        secondary={project[0].projectFrom || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Is this a Clear Lanes project?</b>}
                        secondary={project[0].isClearLanes ? "Yes" : "No"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Project Website</b>}
                        secondary={project[0].website || "None"}
                        secondaryTypographyProps={{
                          sx: {
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal'
                          }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={4} md={4} sx={{ minWidth: 0 }}>
                  <List role="presentation">
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Project Type</b>}
                        secondary={project[0].projectType || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Project To</b>}
                        secondary={project[0].projectTo || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Strategy Type(s)</b>}
                        secondary={project[0]?.strategyTypes?.map(type => type.name).join(",") || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={4} md={4} sx={{ minWidth: 0 }}>
                  <List role="presentation">
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Impacted Region(s)</b>}
                        secondary={getCombRegImpactedRegs(project[0]) || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Is this project a study?</b>}
                        secondary={project[0].isProjectStudy ? "Yes" : "No"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                    <ListItem role="presentation">
                      <ListItemText
                        primary={<b>Planning Document</b>}
                        secondary={project[0].planningDocument || "None"}
                        secondaryTypographyProps={{
                          sx: { wordBreak: 'break-word', overflowWrap: 'break-word' }
                        }}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </StyledPaper>
          </Grid>

          {/* Project Phase Heading */}
          <StyledHeadingGrid item xs={12}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              Project Phase
            </Typography>
          </StyledHeadingGrid>

          {/* Display Project Phase */}
          <Grid item xs={12} md={12}>
            <StyledPaper elevation={3}>
              <Grid item xs={12}>
                <List role="presentation">
                  <ListItem role="presentation">
                    <ListItemText
                      aria-label="Description"
                      primary={<b>Description</b>}
                      secondary={project[0].description || "None"}
                    />
                  </ListItem>
                </List>
              </Grid>
              {projectPhase?.length > 0 && (
                <table style={{ width: 'calc(100% - 32px)', borderCollapse: 'collapse', margin: '8px 16px' }} aria-label="Project Phase Details">
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={{ padding: '8px', paddingLeft: 0 }}>Phase #</th>
                      <th style={{ padding: '8px' }}>CSJ/Plan ID #</th>
                      <th style={{ padding: '8px' }}>{project[0].isProjectStudy ? 'Study Start Date' : 'Let Date'}</th>
                      <th style={{ padding: '8px' }}>Completion Date</th>
                      {project[0].isProjectStudy && (
                        <th style={{ padding: '8px' }}>Study Cost ($)</th>
                      )}
                      <th style={{ padding: '8px' }}>Cost Estimate ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectPhase.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px' }}>{row.phase}</td>
                        <td style={{ padding: '8px' }}>{row.csjNumber || "None"}</td>
                        <td style={{ padding: '8px' }}>
                          {row.letDate ? getFormattedDate(new Date(row.letDate)) : "None"}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {row.completionDate ? getFormattedDate(new Date(row.completionDate)) : "None"}
                        </td>
                        {project[0].isProjectStudy && (
                          <td style={{ padding: '8px' }}>{row.studyCost || "None"}</td>
                        )}
                        <td style={{ padding: '8px' }}>{row.costEstimate || "None"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </StyledPaper>
          </Grid>

          {/* Project Status Heading */}
          <StyledHeadingGrid item xs={12}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              Project Status
            </Typography>
          </StyledHeadingGrid>

          {/* Display Project Status */}
          <Grid item xs={12} md={12}>
            <StyledPaper elevation={3}>

              {/* When project is a study*/}
              {project[0].isProjectStudy && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <List style={{ display: 'flex', flexDirection: 'row' }} role="presentation">
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Scope Identified</b>}
                          secondary={project[0].scopeIdentified || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Funding Acquired</b>}
                          secondary={project[0].fundingAcquired || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Researcher Identified</b>}
                          secondary={project[0].researcherIdentified || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Study In Progress</b>}
                          secondary={project[0].studyInProgress || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Study Complete</b>}
                          secondary={project[0].studyComplete || "None"}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              )}

              {/* When project is not a study*/}
              {!project[0].isProjectStudy && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <List style={{ display: 'flex', flexDirection: 'row' }} role="presentation">
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Feasibility Study</b>}
                          secondary={project[0].feasibilityStudy || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Preliminary Design</b>}
                          secondary={project[0].preliminaryDesign || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>NEPA Review</b>}
                          secondary={project[0].nepaReview || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Final Design</b>}
                          secondary={project[0].finalDesign || "None"}
                        />
                      </ListItem>
                    </List>
                    <List style={{ display: 'flex', flexDirection: 'row' }} role="presentation">
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Funding</b>}
                          secondary={project[0].funding || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Row Acquisition</b>}
                          secondary={project[0].rowAcquisition || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation">
                        <ListItemText
                          primary={<b>Construction</b>}
                          secondary={project[0].construction || "None"}
                        />
                      </ListItem>
                      <ListItem role="presentation" />
                    </List>
                  </Grid>
                </Grid>
              )}
            </StyledPaper>
          </Grid>

          {/* Connected Roadways Heading */}
          <StyledHeadingGrid item xs={12}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              Connected Roadways
            </Typography>
          </StyledHeadingGrid>

          {/* Display connected roadways */}
          <Grid item xs={12} md={12}>
            <StyledPaper elevation={3}>
              {connectedRoadways && connectedRoadways.length > 0 ? (
                <table style={{ width: 'calc(100% - 32px)', borderCollapse: 'collapse', margin: '8px 16px' }} aria-label="Connected Roadways">
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={{ padding: '8px' }}>Roadway Name</th>
                      <th style={{ padding: '8px' }}>From Road</th>
                      <th style={{ padding: '8px' }}>To Road</th>
                      <th style={{ padding: '8px' }}>Impact Area</th>
                      <th style={{ padding: '8px' }}>Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connectedRoadways.map((row, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px' }}>{row.roadName || "None"}</td>
                        <td style={{ padding: '8px' }}>{row.fromRoad || "None"}</td>
                        <td style={{ padding: '8px' }}>{row.toRoad || "None"}</td>
                        <td style={{ padding: '8px' }}>{row.impactArea || "None"}</td>
                        <td style={{ padding: '8px' }}>{row.rank || "None"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Typography variant="body2" sx={{ p: 2 }}>No connected roadways available.</Typography>
              )}
            </StyledPaper>
          </Grid>

          {/* Notes Heading */}
          <StyledHeadingGrid aria-label="Notes Heading" item xs={12}>
            <Typography id="notes-label" variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              Notes
              {isReview && !edit ? (
                <IconButton onClick={handleEditNotes} aria-label="Edit notes">
                  <EditIcon />
                </IconButton>
              ) : null
              }
            </Typography>
            <Typography style={{ fontSize: 12, fontStyle: 'italic' }}>These notes won't be published</Typography>
          </StyledHeadingGrid>

          {/* Display Notes */}
          <Grid item xs={12} md={12}>
            <StyledPaper elevation={3}>
              <StyledGridContainer container spacing={2}>
                <Grid item xs={12}>
                  {!edit ? (
                    <Typography variant="body1">
                      {notes}
                    </Typography>
                  ) : (
                    <TextField
                      id="notes-textfield"
                      autoFocus
                      fullWidth
                      multiline
                      minRows={2}
                      variant='outlined'
                      value={updatedNotes}
                      onChange={handleUpdateNotes}
                      InputProps={{
                        inputComponent: 'textarea'
                      }}
                      inputProps={{
                        id: 'notes-input',
                        'aria-labelledby': 'notes-label'
                      }}
                    />
                  )}
                </Grid>
              </StyledGridContainer>
            </StyledPaper>
          </Grid>

          <Grid item xs={12} md={12}>
            {errors?.length > 0 && (
              <div className="error-messages">
                <Alert severity="error">
                  <strong>Please enter the following required fields by navigating to Edit Project:</strong>
                  <ul>
                    {errors.map((error, index) => (

                      <li key={index}>
                        <strong>{error}</strong>
                      </li>

                    ))}
                  </ul>
                </Alert>
              </div>
            )}
          </Grid>

          {/* Display Buttons as per page*/}
          <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
            {isReview && (
              <>
                <StyledButton aria-label="Cancel changes and go back to View Projects page" variant="contained" color="primary" onClick={handleCancelBtnClick}>
                  Cancel
                </StyledButton>
                <StyledButton aria-label="Mark project as good as is" variant="contained" color="primary" onClick={handleGoodAsIs}>
                  Good As Is
                </StyledButton>
              </>
            )}
            {!isReview && (
              <StyledButton aria-label="Go back to View Projects page" variant="contained" color="primary" onClick={handleCancelBtnClick}>
                Back
              </StyledButton>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
}